import { Menu as MantineMenu, parseThemeColor } from "@mantine/core";
import { componentWrapper, polymorphicComponentWrapper } from "../../utilities";
import styles from "./menu.module.scss";
import type {
    CustomMenuFactory,
    CustomMenuInfoFactory,
    CustomMenuItemFactory,
    CustomMenuSubFactory,
    CustomMenuSubItemFactory,
    CustomMenuSubDropdownFactory,
    IMenuInfoProps,
    IMenuSubDropdownProps,
    IMenuItemProps,
    IMenuSubItemProps,
    IMenuSubProps,
    IProps,
} from "./menu.types";
import { InputComponentProps } from "../input";
import { SearchInput } from "../search-input";
import { useTheme } from "../../hooks";
import { Stack } from "../stack";
import { Text } from "../text";
import { rem } from "@mantine/core";
import { ScrollArea } from "../scroll-area";

/**
 * Menu component for displaying dropdown menus.
 * @param {string | number} props.width - The width of the menu dropdown.
 * @returns {JSX.Element} The Menu component.
 */
const Menu = componentWrapper<CustomMenuFactory, IProps>("Menu", ({ width, ...otherProps }, ref) => {
    return (
        <MantineMenu
            offset={4}
            width={width || rem(220)}
            position="bottom-start"
            classNames={{
                dropdown: styles["dropdown"],
                item: styles["item"],
                itemSection: styles["item-section"],
                itemLabel: styles["item-label"],
                label: styles["label"],
                chevron: styles["chevron"],
            }}
            {...otherProps}
        />
    );
});

/**
 * Menu item component for individual menu options.
 * @param {boolean} props.active - Whether the menu item is in an active state.
 * @param {object} props.style - Custom styles for the menu item.
 * @returns {JSX.Element} The MenuItem component.
 */
export const MenuItem = polymorphicComponentWrapper<CustomMenuItemFactory, IMenuItemProps>(
    "MenuItem",
    ({ active, style, ...other }, ref) => {
        const theme = useTheme();
        const colors = other.color
            ? theme.variantColorResolver({ color: other.color, theme, variant: "light" })
            : undefined;
        const parsedThemeColor = other.color ? parseThemeColor({ color: other.color, theme }) : null;

        return (
            <MantineMenu.Item
                data-active={active}
                data-testid="menu-item"
                {...other}
                ref={ref}
                p="md"
                style={[
                    {
                        "--menu-item-color":
                            parsedThemeColor?.isThemeColor && parsedThemeColor?.shade === undefined
                                ? `var(--mantine-color-${parsedThemeColor.color}-6)`
                                : colors?.color,
                        "--menu-item-section-color":
                            parsedThemeColor?.isThemeColor && parsedThemeColor?.shade === undefined
                                ? `var(--mantine-color-${parsedThemeColor.color}-6)`
                                : colors?.color,
                        "--menu-item-section-color-hover":
                            parsedThemeColor?.isThemeColor && parsedThemeColor?.shade === undefined
                                ? `var(--mantine-color-${parsedThemeColor.color}-6)`
                                : colors?.color,
                        "--menu-item-hover": colors?.hover,
                    },
                    style,
                ]}
            />
        );
    }
);

/**
 * Menu search component for filtering menu items.
 * @param {object} props.inputProps - Props to pass to the search input component.
 * @returns {JSX.Element} The MenuSearch component.
 */
export const MenuSearch = polymorphicComponentWrapper<
    CustomMenuItemFactory,
    Omit<IMenuItemProps, "active" | "renderRoot" | "children"> & { inputProps?: InputComponentProps }
>("MenuSearch", (props, ref) => {
    return (
        <MenuItem
            renderRoot={(props) => {
                return (
                    <SearchInput
                        variant="filled"
                        classNames={{ input: styles["search-input"] }}
                        {...props.inputProps}
                    />
                );
            }}
            {...props}
            ref={ref}
        />
    );
});

/**
 * Menu info component for displaying title and subtitle information in menus.
 * @param {string} props.title - The title text to display.
 * @param {string} props.subtitle - The subtitle text to display.
 * @returns {JSX.Element} The MenuInfo component.
 */
export const MenuInfo = componentWrapper<CustomMenuInfoFactory, Omit<IMenuInfoProps, "renderRoot">>(
    "MenuInfo",
    (props, ref) => {
        return (
            <Menu.Label
                py="md"
                className={styles["info"]}
                renderRoot={({ title, subtitle, ...props }) => {
                    return (
                        <Stack gap="xxs" {...props}>
                            {title && (
                                <Text variant="medium" size="sm">
                                    {title}
                                </Text>
                            )}
                            {subtitle && (
                                <Text variant="medium" size="sm" color="gray">
                                    {subtitle}
                                </Text>
                            )}
                        </Stack>
                    );
                }}
                {...props}
                ref={ref}
            />
        );
    }
);

/**
 * Menu sub component for nested menu sections.
 * @param {boolean} props.active - Whether the submenu is in an active state.
 * @returns {JSX.Element} The MenuSub component.
 */
export const MenuSub = componentWrapper<CustomMenuSubFactory, IMenuSubProps>("MenuSub", ({ active, ...other }, ref) => {
    return <MantineMenu.Sub data-active={active} {...other} />;
});

/**
 * Menu sub dropdown component for nested menu dropdowns with scrolling support.
 * @param {React.ReactNode} props.children - Content to display in the dropdown.
 * @param {string | number} props.mah - Maximum height for the scrollable area.
 * @returns {JSX.Element} The MenuSubDropdown component.
 */
export const MenuSubDropdown = componentWrapper<CustomMenuSubDropdownFactory, IMenuSubDropdownProps>(
    "MenuSubDropdown",
    ({ children, mah, ...other }, ref) => {
        return (
            <MantineMenu.Sub.Dropdown {...other} ref={ref}>
                <ScrollArea.Autosize mah={mah ?? "75vh"}>{children}</ScrollArea.Autosize>
            </MantineMenu.Sub.Dropdown>
        );
    }
);

/**
 * Menu sub item component for items within nested menus.
 * @param {boolean} props.active - Whether the sub menu item is in an active state.
 * @param {object} props.style - Custom styles for the menu item.
 * @returns {JSX.Element} The MenuSubItem component.
 */
export const MenuSubItem = componentWrapper<CustomMenuSubItemFactory, IMenuSubItemProps>(
    "MenuSubItem",
    ({ active, style, ...other }, ref) => {
        const theme = useTheme();
        const colors = other.color
            ? theme.variantColorResolver({ color: other.color, theme, variant: "light" })
            : undefined;
        const parsedThemeColor = other.color ? parseThemeColor({ color: other.color, theme }) : null;

        return (
            <MantineMenu.Sub.Item
                data-active={active}
                data-testid="menu-sub-item"
                {...other}
                ref={ref}
                p="md"
                style={[
                    {
                        "--menu-item-color":
                            parsedThemeColor?.isThemeColor && parsedThemeColor?.shade === undefined
                                ? `var(--mantine-color-${parsedThemeColor.color}-6)`
                                : colors?.color,
                        "--menu-item-section-color":
                            parsedThemeColor?.isThemeColor && parsedThemeColor?.shade === undefined
                                ? `var(--mantine-color-${parsedThemeColor.color}-6)`
                                : colors?.color,
                        "--menu-item-section-color-hover":
                            parsedThemeColor?.isThemeColor && parsedThemeColor?.shade === undefined
                                ? `var(--mantine-color-${parsedThemeColor.color}-6)`
                                : colors?.color,
                        "--menu-item-hover": colors?.hover,
                    },
                    style,
                ]}
                classNames={{ itemLabel: styles["item-label"] }}
            />
        );
    }
);

MenuSub.Target = MantineMenu.Sub.Target;
MenuSub.Dropdown = MantineMenu.Sub.Dropdown;
MenuSub.Item = MenuSubItem;
MenuSub.extend = MantineMenu.Sub.extend;

Menu.Divider = MantineMenu.Divider;
Menu.Dropdown = MantineMenu.Dropdown;
Menu.Item = MenuItem;
Menu.Search = MenuSearch;
Menu.Label = MantineMenu.Label;
Menu.Sub = MenuSub;
Menu.Sub.Dropdown = MenuSubDropdown;
Menu.Sub.Item = MenuSubItem;
Menu.Sub.Target = MantineMenu.Sub.Target;
Menu.Target = MantineMenu.Target;
Menu.Info = MenuInfo;

export { Menu };
